import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export interface GuestDemoState {
  guestId: string;
  scansUsed: number;
  aiChatsUsed: number;
  videoPreviewUsed: boolean;
  firstVisitDate: string;
  demoCompleted: boolean;
  lastShownGate: string | null;
}

interface GuestDemoContextValue {
  demoState: GuestDemoState;
  isGuest: boolean;
  isAuthenticated: boolean;
  isDevMode: boolean;
  canScan: boolean;
  canAIChat: boolean;
  canVideoPreview: boolean;
  hasUsedAnyDemo: boolean;
  useScan: () => boolean;
  useAIChat: () => boolean;
  useVideoPreview: () => boolean;
  markDemoCompleted: () => void;
  resetDemo: () => void;
  showGate: (feature: string) => boolean;
  dismissGate: () => void;
  activeGate: string | null;
}

const DEMO_KEY = 'lavyn_guest_demo_v2';
const GUEST_ID_KEY = 'lavyn_guest_id_v2';
const GATE_COOLDOWN_MS = 2000;

const FREE_SCAN_LIMIT = 1;
const FREE_AI_CHAT_LIMIT = 1;

function generateGuestId(): string {
  return `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getGuestId(): string {
  let id = localStorage.getItem(GUEST_ID_KEY);
  if (!id) {
    id = generateGuestId();
    localStorage.setItem(GUEST_ID_KEY, id);
  }
  return id;
}

function loadDemoState(): GuestDemoState {
  try {
    const raw = localStorage.getItem(DEMO_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<GuestDemoState>;
      return {
        guestId: parsed.guestId || getGuestId(),
        scansUsed: parsed.scansUsed || 0,
        aiChatsUsed: parsed.aiChatsUsed || 0,
        videoPreviewUsed: parsed.videoPreviewUsed || false,
        firstVisitDate: parsed.firstVisitDate || new Date().toISOString(),
        demoCompleted: parsed.demoCompleted || false,
        lastShownGate: parsed.lastShownGate || null,
      };
    }
  } catch { /* ignore */ }
  return {
    guestId: getGuestId(),
    scansUsed: 0,
    aiChatsUsed: 0,
    videoPreviewUsed: false,
    firstVisitDate: new Date().toISOString(),
    demoCompleted: false,
    lastShownGate: null,
  };
}

function saveDemoState(state: GuestDemoState) {
  try {
    localStorage.setItem(DEMO_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

// ── Dev Mode Detection ──
// In development builds, all guest demo restrictions are bypassed so
// developers can test flows without repeatedly clearing localStorage.
// Production builds always enforce the one-time demo limits.
const isDevBuild = () => {
  try {
    return import.meta.env.DEV === true;
  } catch {
    return false;
  }
};

const GuestDemoContext = createContext<GuestDemoContextValue | null>(null);

export function GuestDemoProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [demoState, setDemoState] = useState<GuestDemoState>(loadDemoState);
  const [activeGate, setActiveGate] = useState<string | null>(null);

  const isDevMode = isDevBuild();

  // Persist on change
  useEffect(() => {
    saveDemoState(demoState);
  }, [demoState]);

  // Real auth-aware guest detection
  const isGuest = !isAuthenticated;

  // In dev mode, always allow everything for frictionless testing
  const canScan = isDevMode || !isGuest || demoState.scansUsed < FREE_SCAN_LIMIT;
  const canAIChat = isDevMode || !isGuest || demoState.aiChatsUsed < FREE_AI_CHAT_LIMIT;
  const canVideoPreview = isDevMode || !isGuest || !demoState.videoPreviewUsed;
  const hasUsedAnyDemo = demoState.scansUsed > 0 || demoState.aiChatsUsed > 0 || demoState.videoPreviewUsed;

  const showGate = useCallback((feature: string): boolean => {
    // Never show gates in dev mode or for authenticated users
    if (isDevMode || !isGuest) return false;

    // If already showing this gate, don't re-show
    if (activeGate === feature) return true;
    // If recently shown, respect cooldown
    if (demoState.lastShownGate) {
      const lastShown = new Date(demoState.lastShownGate.split('|')[1] || 0);
      if (Date.now() - lastShown.getTime() < GATE_COOLDOWN_MS) return false;
    }
    setActiveGate(feature);
    setDemoState((s) => ({
      ...s,
      lastShownGate: `${feature}|${new Date().toISOString()}`,
    }));
    return true;
  }, [activeGate, demoState.lastShownGate, isDevMode, isGuest]);

  const dismissGate = useCallback(() => {
    setActiveGate(null);
  }, []);

  const useScan = useCallback((): boolean => {
    // Authenticated users and dev mode bypass counters entirely
    if (isAuthenticated || isDevMode) return true;
    if (canScan) {
      setDemoState((s) => {
        const next = { ...s, scansUsed: s.scansUsed + 1 };
        // Auto-mark completed if all used up
        if (next.scansUsed >= FREE_SCAN_LIMIT && next.aiChatsUsed >= FREE_AI_CHAT_LIMIT) {
          next.demoCompleted = true;
        }
        saveDemoState(next);
        return next;
      });
      return true;
    }
    showGate('scan');
    return false;
  }, [canScan, showGate, isAuthenticated, isDevMode]);

  const useAIChat = useCallback((): boolean => {
    if (isAuthenticated || isDevMode) return true;
    if (canAIChat) {
      setDemoState((s) => {
        const next = { ...s, aiChatsUsed: s.aiChatsUsed + 1 };
        if (next.scansUsed >= FREE_SCAN_LIMIT && next.aiChatsUsed >= FREE_AI_CHAT_LIMIT) {
          next.demoCompleted = true;
        }
        saveDemoState(next);
        return next;
      });
      return true;
    }
    showGate('ai_chat');
    return false;
  }, [canAIChat, showGate, isAuthenticated, isDevMode]);

  const useVideoPreview = useCallback((): boolean => {
    if (isAuthenticated || isDevMode) return true;
    if (canVideoPreview) {
      setDemoState((s) => {
        const next = { ...s, videoPreviewUsed: true };
        saveDemoState(next);
        return next;
      });
      return true;
    }
    showGate('video_preview');
    return false;
  }, [canVideoPreview, showGate, isAuthenticated, isDevMode]);

  const markDemoCompleted = useCallback(() => {
    setDemoState((s) => {
      const next = { ...s, demoCompleted: true };
      saveDemoState(next);
      return next;
    });
  }, []);

  const resetDemo = useCallback(() => {
    const fresh: GuestDemoState = {
      guestId: generateGuestId(),
      scansUsed: 0,
      aiChatsUsed: 0,
      videoPreviewUsed: false,
      firstVisitDate: new Date().toISOString(),
      demoCompleted: false,
      lastShownGate: null,
    };
    setDemoState(fresh);
    saveDemoState(fresh);
    localStorage.setItem(GUEST_ID_KEY, fresh.guestId);
    // Also clear session gate memory so returning gate can re-fire after reset
    sessionStorage.removeItem('lavyn_returning_gate_shown');
  }, []);

  return (
    <GuestDemoContext.Provider
      value={{
        demoState,
        isGuest,
        isAuthenticated,
        isDevMode,
        canScan,
        canAIChat,
        canVideoPreview,
        hasUsedAnyDemo,
        useScan,
        useAIChat,
        useVideoPreview,
        markDemoCompleted,
        resetDemo,
        showGate,
        dismissGate,
        activeGate,
      }}
    >
      {children}
    </GuestDemoContext.Provider>
  );
}

export function useGuestDemo() {
  const ctx = useContext(GuestDemoContext);
  if (!ctx) throw new Error('useGuestDemo must be used within GuestDemoProvider');
  return ctx;
}